import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "@material-ui/core";
import { CloseButton } from "../../pages/dashboard/styles";
import { StyledPaper } from "../addBankPopup/styles";
import { FunctionalButton } from "../functionalButton";
import { FormTitle } from "../../pages/homePage/styles";
import PledgxLogo from "assets/common/pledgxLogo";
import CloseIcon from "@material-ui/icons/Close";
import { naicsCodes } from "../../pages/underwriters/data/naicsCodes";
import Select from "../selectField";
import Input from "../inputField";
import DateTimePickerField from "../dateTimePickerField";
import { SmallTitleText } from "../../pages/underwriterDashboard/idpDocuments/styles";
import Success from "../../assets/dashboardIcons/Success";
import { AlertPopup } from "../alertPopup/alertPopup";
import { InputLabel } from "./styles";
import { CategoriesRow } from "../commonCategories/CategoriesRow";
import { useDispatch, useSelector } from "react-redux";
import {
  standardCategoriesListSelector,
  unspcFamilyListSelector,
} from "../../data/selectors/user";
import { underwriterClientSelector } from "../../data/selectors/underwriters";
import {
  getStandardCategoriesInfo,
  getUNSPCClassInfo,
} from "../../data/actions/user";
import {
  PlusSignArea,
  SmallAddText,
  StyledAddText,
  StyledCategoriesArea,
} from "../commonCategories/styles";
import PlusSign from "../../assets/onBoarding/plusSign";
import { StyledSubtext } from "../../pages/userOnBoardingPage/components/styles";
import RadioGroupButtonForProjectCategory from "../radioGroupButtonForProjectCategory";
import { AddressAutoFields } from "../addClientPopup/AddressAutoFields";
import { addNewAccountForUnderwriter, editAccountForUnderwriter } from "../../api/external_data";
import { addUnderwriterAccount, editUnderwriterAccount, getUnderwriterClientInfo } from "../../data/actions/underwriters";
import { SmallText } from "pages/newPreQualify/components/styles";
import InsufficientCreditsPopup from "pages/underwriters/components/insufficientCreditsPopup";
import { entityCreditsSelector } from "data/selectors/user";
import { getEntityCredits, setEntityCredits } from "data/actions/user";
import functionalCreditCosts from "../../pages/underwriterDashboard/functionalCreditCosts.json";

const convertToYYYYMMDD = (dateString) => {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);

  // Get year, month, and day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  // Return formatted date string
  return `${year}-${month}-${day}`;
};

export const EditAccountPopup = ({ open, setOpen, client }) => {
  const [form, setForm] = useState({
    accountId: "",
    companyName: "",
    submissionDate: "",
    accountType: "",
    address: "",
    uwOffice: "",
    contractorType: "",
    contractorGroup: "",
    financialYeDate: "",
    naicsCode: "",
  });
  const [fullAddress, setFullAddress] = useState({
    address: "",
    suite: "",
    postal: "",
    city: "",
    province: "",
    country: "",
  });

  const [error, setError] = useState("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [dialogKey, setDialogKey] = useState(0);

  const standardCategoriesList = useSelector(standardCategoriesListSelector);
  const unspcFamilyList = useSelector(unspcFamilyListSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (standardCategoriesList?.length < 1) {
      dispatch(getStandardCategoriesInfo());
    }
  }, [dispatch, standardCategoriesList]);

  useEffect(() => {
    if (unspcFamilyList?.length < 1) {
      dispatch(getUNSPCClassInfo());
    }
  }, [dispatch, unspcFamilyList]);

  useEffect(() => {
    if (open && client && client.AccountId) {
      console.log("EditAccountPopup: Populating form with client data", client);
      // Increment dialog key to force re-render of all input components
      setDialogKey(prev => prev + 1);
      
      // Always populate the form when client data is available and dialog is open
      setForm({
        accountId: client?.AccountId ?? "",
        companyName: client?.CompanyName ?? "",
        submissionDate: convertToYYYYMMDD(client?.SubmissionDate) ?? "",
        accountType: client?.AccountType ?? "",
        address: client?.CompanyAddress ?? "",
        uwOffice: client?.Office ?? "",
        contractorType: client?.ContractorType ?? "",
        contractorGroup: client?.ContractorGroup ?? "",
        financialYeDate: convertToYYYYMMDD(client?.FinancialYEDate) ?? "",
        naicsCode: client?.NaicsCode ?? "",
      });
      setFullAddress({
        address: client?.CompanyAddress ?? "",
        suite: client?.Suite ?? "",
        postal: client?.PostalCode ?? "",
        city: client?.City ?? "",
        province: client?.Province ?? "",
        country: client?.Country ?? "",
      });

      // Parse categories from JSON strings
      if (client?.ContractorGroup && client?.ContractorType) {
        try {
          const categoryGroups = JSON.parse(client.ContractorGroup);
          const categoryTypes = JSON.parse(client.ContractorType);
  
          const parsedCategories = categoryGroups.map((group, groupIndex) => {
            // For each main category, find its corresponding sub-categories
            const subCategories = [];
            
            // Look through all category types to find sub-categories for this main category
            categoryTypes.forEach((typeObj) => {
              // Check if this type object has sub-categories for the current main category
              const subCategoryKey = `Sub-Category-${groupIndex + 1}`;
              if (typeObj[subCategoryKey]) {
                subCategories.push(typeObj[subCategoryKey]);
              }
            });
            
            return {
              main_categories: group,
              sub_categories: [...new Set(subCategories)], // Remove duplicates
              index: groupIndex
            };
          });
          
          setCategories(parsedCategories);
        } catch (error) {
          console.error("Error parsing categories:", error);
          setCategories([]);
        }
      } else {
        setCategories([]);
      }
    }
  }, [client?.AccountId, open]);



  const handleInputChange = (field, value) => {
    console.log("EditAccountPopup handleInputChange called - field: ", field, "value: ", value);
    setForm((prevForm) => ({
      ...prevForm,
      [field]: value,
    }));
  };

  const handleSubmit = async (form) => {
    const createCategories = () => {
      const filteredCategories = categories.filter((category) => {
        if (
          category.main_categories !== "" &&
          category.sub_categories &&
          category.sub_categories?.length > 0
        ) {
          return category;
        }
      });

      const categoryGroups = [
        ...new Set(
          filteredCategories.map((category) => category.main_categories),
        ),
      ];

      const categoryTypes = filteredCategories.flatMap((category) => {
        return category.sub_categories.map((sub, index) => {
          return {
            [`Sub-Category-${index + 1}`]: sub,
          };
        });
      });

      const categoryGroupsString = JSON.stringify(categoryGroups);
      const categoryTypesString = JSON.stringify(categoryTypes);
      return { categoryGroupsString, categoryTypesString };
    };

    const { categoryGroupsString, categoryTypesString } = createCategories();

    try {
      await editAccountForUnderwriter(
        {
          ...form,
          accountId: parseInt(form.accountId),
          contractorGroup: categoryGroupsString,
          contractorType: categoryTypesString,
          address:
            fullAddress.address +
            " " +
            fullAddress.suite +
            " " +
            fullAddress.postal +
            " " +
            fullAddress.city +
            " " +
            fullAddress.province +
            " " +
            fullAddress.country,
        },
        (response) => {
          setOpen(false);
          setShowSuccessDialog(true);
          dispatch(editUnderwriterAccount({ ...response })); 
          console.log("Successfully updated the account!");
        },
        ({ data }) => {
          console.warn("Failed to update the account: ", data.error);
          setError(
            data.error ||
              "Unknown error, please contact PledgX support for assistance.",
          );
          setShowErrorDialog(true);
        },
      );
    } catch (e) {
      console.log("Exception in updateAccountForUnderwriter: ", e);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="edit-account-dialog"
        aria-describedby="dialog to edit account details"
        maxWidth="md"
        TransitionComponent={React.Fragment}
      >
        <StyledPaper elevation={0}>
          <PledgxLogo />
          <FormTitle style={{ paddingTop: "18px" }}>Edit Account</FormTitle>
          <SmallTitleText>
            {client?.CompanyName ?? "Unknown Company"}
          </SmallTitleText>
          <CloseButton onClick={() => setOpen(false)} aria-label="close">
            <CloseIcon />
          </CloseButton>
          {console.log("EditAccountPopup categories state:", categories)}
          <div
            style={{
              overflowY: "auto",
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              flexDirection: "column",
              maxHeight: "calc(100vh - 200px)",
              paddingTop: "20px",
              boxSizing: "border-box",
            }}
          >
            <div style={{ padding: "20px 0", width: "100%" }}>
              <div
                style={{ display: "flex", flexDirection: "row", gap: "16px" }}
              >
                <Input
                  key={`companyName-${dialogKey}-${client?.AccountId}`}
                  id="input-companyName"
                  label="Account Name"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => Boolean(item)}
                  setValue={(e) => handleInputChange("companyName", e)}
                  value={form.companyName}
                />
              </div>


              <div style={{ display: "flex", flexDirection: "row", gap: "16px", marginTop: "16px" }}>
                <Input
                  key={`address-${dialogKey}-${client?.AccountId}`}
                  id="input-address"
                  label="Address"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => Boolean(item)}
                  setValue={(e) => setFullAddress(prev => ({ ...prev, address: e }))}
                  value={fullAddress.address}
                />
                <Input
                  key={`suite-${dialogKey}-${client?.AccountId}`}
                  id="input-suite"
                  label="Suite"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => Boolean(item)}
                  setValue={(e) => setFullAddress(prev => ({ ...prev, suite: e }))}
                  value={fullAddress.suite}
                />
                <Input
                  key={`postal-${dialogKey}-${client?.AccountId}`}
                  id="input-postal"
                  label="Postal Code"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => Boolean(item)}
                  setValue={(e) => setFullAddress(prev => ({ ...prev, postal: e }))}
                  value={fullAddress.postal}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "row", gap: "16px", marginTop: "16px" }}>
                <Input
                  key={`city-${dialogKey}-${client?.AccountId}`}
                  id="input-city"
                  label="City"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => Boolean(item)}
                  setValue={(e) => setFullAddress(prev => ({ ...prev, city: e }))}
                  value={fullAddress.city}
                />
                <Input
                  key={`province-${dialogKey}-${client?.AccountId}`}
                  id="input-province"
                  label="Province"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => Boolean(item)}
                  setValue={(e) => setFullAddress(prev => ({ ...prev, province: e }))}
                  value={fullAddress.province}
                />
                <Input
                  key={`country-${dialogKey}-${client?.AccountId}`}
                  id="input-country"
                  label="Country"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => Boolean(item)}
                  setValue={(e) => setFullAddress(prev => ({ ...prev, country: e }))}
                  value={fullAddress.country}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  marginTop: "16px",
                }}
              >
                <Input
                  key={`accountId-${dialogKey}-${client?.AccountId}`}
                  id="input-accountId"
                  label="Account ID"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => Boolean(item)}
                  setValue={(e) => handleInputChange("accountId", e)}
                  value={form?.accountId ?? ""}
                />
                <Select
                  key={`accountType-${dialogKey}-${client?.AccountId}`}
                  label="Account Type"
                  options={["Contract", "Commercial", "Other"].map((type) => ({
                    value: type,
                    label: type,
                  }))}
                  value={form.accountType}
                  setValue={(e) => handleInputChange("accountType", e)}
                  noCheckIcon={true}
                  showDropdownIndicator={true}
                  validateFunc={(item) => Boolean(item)}
                />
                <Input
                  key={`uwOffice-${dialogKey}-${client?.AccountId}`}
                  id="input-uwOffice"
                  label="Underwriter Office"
                  type="text"
                  labelSize="12px"
                  fontSize="18px"
                  validateFunc={(item) => item.length > 0}
                  setValue={(e) => handleInputChange("uwOffice", e)}
                  value={form.uwOffice}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  marginTop: "16px",
                }}
              >
                <DateTimePickerField
                  key={`submissionDate-${dialogKey}-${client?.AccountId}`}
                  showDateField={true}
                  dateWidth="100%"
                  dateLabel="Submission Date"
                  backgroundColor="rgba(235, 233, 241, 0.75)"
                  dateValue={form.submissionDate}
                  setDateValue={(e) => handleInputChange("submissionDate", e)}
                />
                <DateTimePickerField
                  key={`financialYeDate-${dialogKey}-${client?.AccountId}`}
                  showDateField={true}
                  dateWidth="100%"
                  dateLabel="Financial YE Date"
                  backgroundColor="rgba(235, 233, 241, 0.75)"
                  dateValue={form.financialYeDate}
                  setDateValue={(e) => handleInputChange("financialYeDate", e)}
                />
                <Select
                  key={`naicsCode-${dialogKey}-${client?.AccountId}`}
                  label="NAICS Code"
                  options={naicsCodes.map((code) => ({
                    value: code,
                    label: code,
                  }))}
                  value={form.naicsCode}
                  setValue={(e) => handleInputChange("naicsCode", e)}
                  noCheckIcon={true}
                  showDropdownIndicator={true}
                  validateFunc={(item) => Boolean(item)}
                />
              </div>

              {/* <div style={{ width: "100%", marginTop: "16px" }}>
                <CategorySelect
                  data={{ categories: categories }}
                  setData={(attribute, val) => {
                    console.log("CategorySelect setData called with:", attribute, val);
                    setCategories((old) => [...old, val]);
                  }}
                  notPopup={true}
                  categoriesStandardData={standardCategoriesList}
                  categoriesUNSPCData={unspcFamilyList}
                  containsUNSPC={true}
                />
              </div> */}
              <div
                style={{
                  margin: "40px 0",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <FunctionalButton
                  buttonTitle="Update"
                  handleButton={() => handleSubmit(form)}
                />
              </div>
            </div>
          </div>
        </StyledPaper>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        aria-labelledby="success-dialog"
        aria-describedby="success-dialog"
        maxWidth="md"
        TransitionComponent={React.Fragment}
      >
        <StyledPaper elevation={0}>
          <CloseButton
            aria-label="close"
            onClick={() => setShowSuccessDialog(false)}
          >
            <CloseIcon />
          </CloseButton>
          <Success />
          <FormTitle
            style={{
              marginTop: "10px",
              color: "#1A051D",
              fontSize: "22px",
              fontWeight: "600",
              lineHeight: "24px",
            }}
          >
            Success
          </FormTitle>
          <SmallTitleText
            style={{
              marginBottom: 10,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            The account has been successfully updated.
          </SmallTitleText>
          <FunctionalButton
            buttonTitle="Close"
            handleButton={() => setShowSuccessDialog(false)}
            width="308px"
            height="48px"
            isReversedColor={false}
            marginRight={"48px"}
            marginLeft={"48px"}
            marginTop={"30px"}
            marginBottom={"20px"}
            fontSize={"18px"}
            disabled={false}
            icon={null}
            hasBoxShadow={true}
            frontIcon={null}
            weight={600}
          />
        </StyledPaper>
      </Dialog>

      {/* Error Dialog */}
      <AlertPopup
        headerText="Account Update Unsuccessful"
        height="434px"
        subHeaderText={error}
        openPopup={showErrorDialog}
        setOpenPopup={setShowErrorDialog}
        showIcon={true}
        width={"640px"}
        showButton={false}
      />
    </>
  );
};

export const AcceptAsAccountPopup = ({ open, setOpen, accountData }) => {
  const [form, setForm] = useState({
    accountId: accountData?.accountId || "",
    companyName: accountData?.companyName || "",
    submissionDate: accountData?.submissionDate || "",
    accountType: accountData?.accountType || "",
    address: accountData?.address || "",
    uwOffice: accountData?.uwOffice || "",
    contractorType: accountData?.contractorType || "",
    contractorGroup: accountData?.contractorGroup || "",
    financialYeDate: accountData?.financialYeDate || "",
    naicsCode: accountData?.naicsCode || "",
  });
  const [categories, setCategories] = useState([]);
  const [fullAddress, setFullAddress] = useState({
    address: accountData?.address || "",
    suite: accountData?.suite || "",
    postal: accountData?.postal || "",
    city: accountData?.city || "",
    province: accountData?.province || "",
    country: accountData?.country || "",
  });

  const [error, setError] = useState("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showInsufficientCreditsDialog, setShowInsufficientCreditsDialog] = useState(false);

  const standardCategoriesList = useSelector(standardCategoriesListSelector);
  const unspcFamilyList = useSelector(unspcFamilyListSelector);

  const dispatch = useDispatch();
  const entityCredits = useSelector(entityCreditsSelector);
  const underwriterClientList = useSelector(underwriterClientSelector);

  const maxFreeUsers = 25;

  useEffect(() => {
    if (underwriterClientList === null) {
      dispatch(getUnderwriterClientInfo());
    }
  }, [dispatch, underwriterClientList]);
  
  useEffect(() => {
    if (entityCredits == null) {
      dispatch(getEntityCredits(null));
    }
  }, [entityCredits, dispatch]);

  // useEffect(() => {
  //   setForm((form) => {...form, companyName: accountName });
  // }, [accountName]);

  useEffect(() => {
    if (standardCategoriesList?.length < 1) {
      dispatch(getStandardCategoriesInfo());
    }
  }, [dispatch]);

  // Update form and address when accountData changes
  useEffect(() => {
    if (accountData && open) {
      console.log("AcceptAsAccountPopup received accountData:", accountData);
      setForm({
        accountId: accountData.accountId || "",
        companyName: accountData.companyName || "",
        submissionDate: accountData.submissionDate || "",
        accountType: accountData.accountType || "",
        address: accountData.address || "",
        uwOffice: accountData.uwOffice || "",
        contractorType: accountData.contractorType || "",
        contractorGroup: accountData.contractorGroup || "",
        financialYeDate: accountData.financialYeDate || "",
        naicsCode: accountData.naicsCode || "",
      });
      
      const addressData = {
        address: accountData.address || "",
        suite: accountData.suite || "",
        postal: accountData.postal || "",
        city: accountData.city || "",
        province: accountData.province || "",
        country: accountData.country || "",
      };
      console.log("Setting fullAddress:", addressData);
      setFullAddress(addressData);
    }
  }, [accountData, open]);

  useEffect(() => {
    if (unspcFamilyList?.length < 1) {
      dispatch(getUNSPCClassInfo());
    }
  }, [dispatch]);

  const handleInputChange = (field, value) => {
    setForm((prevForm) => ({
      ...prevForm,
      [field]: value,
    }));
  };

  const handleSubmit = async (form) => {
    if (underwriterClientList.length >= maxFreeUsers && entityCredits < functionalCreditCosts.accounts.addNewAccount) {
      setShowInsufficientCreditsDialog(true);
      return;
    } 

    const createCategories = () => {
      const filteredCategories = categories.filter((category) => {
        if (
          category[0].main_categories !== "" &&
          category[0].sub_categories &&
          category[0].sub_categories?.length > 0
        ) {
          return category[0];
        }
      });

      const categoryGroups = [
        ...new Set(
          filteredCategories.map((category) => category[0].main_categories),
        ),
      ];

      const categoryTypes = filteredCategories.flatMap((category) => {
        return category[0].sub_categories.map((sub, index) => {
          return {
            [`Sub-Category-${index + 1}`]: sub,
          };
        });
      });

      const categoryGroupsString = JSON.stringify(categoryGroups);
      const categoryTypesString = JSON.stringify(categoryTypes);

      return { categoryGroupsString, categoryTypesString };
    };

    const { categoryGroupsString, categoryTypesString } = createCategories();

    console.log("full address: ", fullAddress);

    try {
      await addNewAccountForUnderwriter(
        {
          ...form,
          accountId: parseInt(form.accountId),
          contractorGroup: categoryGroupsString,
          contractorType: categoryTypesString,
          address: fullAddress.address,
          country: fullAddress.country,
          suite: fullAddress.suite,
          city: fullAddress.city,
          province: fullAddress.province,
          postalCode: fullAddress.postal,
        },
        (response) => {
          setOpen(false);
          setShowSuccessDialog(true);
          dispatch(addUnderwriterAccount(response));
          dispatch(setEntityCredits({ credits: entityCredits - functionalCreditCosts.accounts.addNewAccount }));
          console.log("Successfully added the new underwrite accountr!");
        },
        ({ data }) => {
          console.warn("Failed to add the new underwriter: ", data.error);
          setError(
            data.error ||
              "Unknown error, please contact PledgX support for assistance.",
          );
          setShowErrorDialog(true);
        },
      );
    } catch (e) {
      console.log("Exception in addNewAccountForUnderwriter: ", e);
    }
  };

  return (
    <>
      <InsufficientCreditsPopup open={showInsufficientCreditsDialog} setOpen={setShowInsufficientCreditsDialog} />
      <Dialog
        open={open}
        setOpen={setOpen}
        aria-labelledby="add-new-account-dialog"
        aria-describedby="diaog to add new account"
        maxWidth="md"
        TransitionComponent={React.Fragment}
      >
        <StyledPaper elevation={0}>
          <PledgxLogo />
          <FormTitle style={{ padding: "18px 18px 0 18px" }}>Accept as Account</FormTitle>
          <SmallTitleText>{form.companyName}</SmallTitleText>
          <CloseButton onClick={() => setOpen(false)} aria-label="close">
            <CloseIcon />
          </CloseButton>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: "16px",
            }}
          >
            <Input
              id="fullWidthInputWrap"
              label="Account Name"
              type="text"
              labelSize="12px"
              fontSize="18px"
              validateFunc={(item) => Boolean(item)}
              setValue={(e) => handleInputChange("companyName", e)}
              value={form.companyName}
            />
          </div>

          <AddressAutoFields
            data={fullAddress}
            setData={(e, val) => {
              console.log("e: ", e);
              console.log("val: ", val);
              setFullAddress((address) => {
                if (e === "company") {
                  return {
                    city: val.city,
                    address: val.address,
                    suite: val.suite,
                    postal: val.postal,
                    country: val.country,
                    province: val.province,
                  };
                }

                return {
                  ...address,
                  [e.split(".").slice(1).join(".")]: val,
                };
              });
            }}
            hasMargin
          />

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: "16px",
            }}
          >
            <Input
              id="fullWidthInputWrap"
              label="Account ID"
              type="text"
              labelSize="12px"
              fontSize="18px"
              validateFunc={(item) => Boolean(item)}
              setValue={(e) => handleInputChange("accountId", e)}
              value={form.accountId}
            />
            <Select
              label="Account Type"
              options={(() =>
                ["Contract", "Commercial", "Other"].map((type) => ({
                  value: type,
                  label: type,
                })))()}
              value={form.accountType}
              setValue={(e) => handleInputChange("accountType", e)}
              noCheckIcon={true}
              showDropdownIndicator={true}
              validateFunc={(item) => Boolean(item)}
            />
            <Input
              id="fullWidthInputWrap"
              label="Underwriter Office"
              type="text"
              labelSize="12px"
              fontSize="18px"
              validateFunc={(item) => item.length > 0}
              setValue={(e) => handleInputChange("uwOffice", e)}
              value={form.uwOffice}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: "16px",
            }}
          >
            <DateTimePickerField
              showDateField={true}
              dateWidth="100%"
              dateLabel="Submission Date"
              backgroundColor="rgba(235, 233, 241, 0.75)"
              dateValue={form.submissionDate}
              setDateValue={(e) => handleInputChange("submissionDate", e)}
            />
            <DateTimePickerField
              showDateField={true}
              dateWidth="100%"
              dateLabel="Financial YE Date"
              backgroundColor="rgba(235, 233, 241, 0.75)"
              dateValue={form.financialYeDate}
              setDateValue={(e) => handleInputChange("financialYeDate", e)}
            />
            <Select
              label="NAICS Code"
              options={(() =>
                naicsCodes.map((code) => ({
                  value: code,
                  label: code,
                })))()}
              value={form.naicsCode}
              setValue={(e) => handleInputChange("naicsCode", e)}
              noCheckIcon={true}
              showDropdownIndicator={true}
              validateFunc={(item) => Boolean(item)}
            />
          </div>
          <div style={{ width: "100%" }}>
            <CategorySelect
              data={form.contractorType}
              setData={(attribute, val) => {
                setCategories((old) => [...old, val]);
              }}
              notPopup={true}
              categoriesStandardData={standardCategoriesList}
              categoriesUNSPCData={unspcFamilyList}
              containsUNSPC={true}
            />
          </div>
          <div
            style={{
              margin: "40px 0",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <FunctionalButton
              buttonTitle="Confirm"
              handleButton={() => handleSubmit(form)}
              // disabled={
              //   !form.accountId ||
              //   !form.companyName ||
              //   !form.submissionDate ||
              //   !form.accountType ||
              //   !form.address ||
              //   !form.uwOffice ||
              //   !form.financialYeDate ||
              //   !form.naicsCode
              // }
            />
          </div>
        </StyledPaper>
      </Dialog>
      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        aria-labelledby="success-dialog"
        aria-describedby="success-dialog"
        maxWidth="md"
        TransitionComponent={React.Fragment}
      >
        <StyledPaper elevation={0}>
          <CloseButton
            aria-label="close"
            onClick={() => setShowSuccessDialog(false)}
          >
            <CloseIcon />
          </CloseButton>
          <Success />
          <FormTitle
            style={{
              marginTop: "10px",
              color: "#1A051D",
              fontSize: "22px",
              fontWeight: "600",
              fontStyle: "normal",
              lineHeight: "24px",
            }}
          >
            Success
          </FormTitle>
          <SmallTitleText
            style={{
              marginBottom: 10,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            The account has been successfully created.
          </SmallTitleText>
          <FunctionalButton
            buttonTitle="Close"
            handleButton={() => setShowSuccessDialog(false)}
            width="308px"
            height="48px"
            isReversedColor={false}
            marginRight={"48px"}
            marginLeft={"48px"}
            marginTop={"30px"}
            marginBottom={"20px"}
            fontSize={"18px"}
            disabled={false}
            icon={null}
            hasBoxShadow={true}
            frontIcon={null}
            weight={600}
          />
        </StyledPaper>
      </Dialog>

      {/* Error Dialog */}
      <AlertPopup
        headerText="Account Creation Unsuccessful"
        height="434px"
        subHeaderText={error}
        openPopup={showErrorDialog}
        setOpenPopup={setShowErrorDialog}
        showIcon={true}
        width={"640px"}
        showButton={false}
      />
    </>
  );
};

export const AddAccountPopup = ({ open, setOpen }) => {
  console.log("open ", open);
  const [form, setForm] = useState({
    accountId: "",
    companyName: "",
    submissionDate: "",
    accountType: "",
    address: "",
    uwOffice: "",
    contractorType: "",
    contractorGroup: "",
    financialYeDate: "",
    naicsCode: "",
  });

  const [fullAddress, setFullAddress] = useState({
    address: "",
    suite: "",
    postal: "",
    city: "",
    province: "",
    country: "",
  });

  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showInsufficientCreditsPopup, setShowInsufficientCreditsPopup] = useState(false);

  const titles = [
    "Add A New Account",
    "Enter Account Details",
    "Invite A New Principle To Onboard",
  ];

  const standardCategoriesList = useSelector(standardCategoriesListSelector);
  const unspcFamilyList = useSelector(unspcFamilyListSelector);

  const dispatch = useDispatch();
  const entityCredits = useSelector(entityCreditsSelector);
  const underwriterClientList = useSelector(underwriterClientSelector);

  const maxFreeUsers = 25;

  useEffect(() => {
    if (underwriterClientList == null) {
      dispatch(getUnderwriterClientInfo());
    }
  }, [underwriterClientList]);
  
  useEffect(() => {
    if (entityCredits == null) {
      dispatch(getEntityCredits(null));
    }
  }, [entityCredits, dispatch]);

  useEffect(() => {
    if (standardCategoriesList?.length < 1) {
      dispatch(getStandardCategoriesInfo());
    }
  }, []);

  useEffect(() => {
    if (unspcFamilyList?.length < 1) {
      dispatch(getUNSPCClassInfo());
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setForm({
        accountId: "",
        companyName: "",
        submissionDate: "",
        accountType: "",
        address: "",
        uwOffice: "",
        contractorType: "",
        contractorGroup: "",
        financialYeDate: "",
        naicsCode: "",
      });
    }
  }, [open]);

  const handleInputChange = (field, value) => {
    setForm((prevForm) => ({
      ...prevForm,
      [field]: value,
    }));
  };

  const handleSubmit = async (form) => {
    if (underwriterClientList.length >= maxFreeUsers && entityCredits < functionalCreditCosts.accounts.addNewAccount) {
      setShowInsufficientCreditsPopup(true);
      return;
    }

    const createCategories = () => {
      const filteredCategories = categories.filter((category) => {
        if (
          category.main_categories !== "" &&
          category.sub_categories &&
          category.sub_categories?.length > 0
        ) {
          return category;
        }
      });

      const categoryGroups = [
        ...new Set(
          filteredCategories.map((category) => category.main_categories),
        ),
      ];

      const categoryTypes = filteredCategories.flatMap((category) => {
        return category.sub_categories.map((sub, index) => {
          return {
            [`Sub-Category-${index + 1}`]: sub,
          };
        });
      });

      const categoryGroupsString = JSON.stringify(categoryGroups);
      const categoryTypesString = JSON.stringify(categoryTypes);

      return { categoryGroupsString, categoryTypesString };
    };

    const { categoryGroupsString, categoryTypesString } = createCategories();

    console.log("full address: ", fullAddress);

    try {
      await addNewAccountForUnderwriter(
        {
          ...form,
          accountId: parseInt(form.accountId),
          contractorGroup: categoryGroupsString,
          contractorType: categoryTypesString,
          address: fullAddress.address,
          country: fullAddress.country,
          suite: fullAddress.suite,
          city: fullAddress.city,
          province: fullAddress.province,
          postalCode: fullAddress.postal,
        },
        (response) => {
          setOpen(false);
          setShowSuccessDialog(true);
          dispatch(addUnderwriterAccount(response));
          dispatch(setEntityCredits({ credits: entityCredits - functionalCreditCosts.accounts.addNewAccount }));
          console.log("Successfully added the new underwriter account!");
        },
        ({ data }) => {
          console.warn("Failed to add the new underwriter: ", data.error);
          setError(
            data.error ||
              "Unknown error, please contact PledgX support for assistance.",
          );
          setShowErrorDialog(true);
        },
      );
    } catch (e) {
      console.log("Exception in addNewAccountForUnderwriter: ", e);
    }
  };

  return (
    <>
    <InsufficientCreditsPopup open={showInsufficientCreditsPopup} setOpen={setShowInsufficientCreditsPopup} />
      <Dialog
        open={open}
        setOpen={setOpen}
        aria-labelledby="add-new-account-dialog"
        aria-describedby="diaog to add new account"
        maxWidth="md"
        TransitionComponent={React.Fragment}
      >
        <StyledPaper elevation={0}>
          <PledgxLogo />
          <FormTitle style={{ padding: "18px" }}>{titles[step]}</FormTitle>
          <CloseButton onClick={() => setOpen(false)} aria-label="close">
            <CloseIcon />
          </CloseButton>
          <div
            style={{
              overflowY: "auto",
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              flexDirection: "column",
              maxHeight: "calc(100vh - 200px)",
              paddingTop: "20px",
              boxSizing: "border-box",
            }}
          >
            {step === 0 && (
              <div
                style={{
                  padding: "20px 0",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ marginBottom: "16px" }}>
                  <FunctionalButton
                    buttonTitle="Add Manually"
                    handleButton={() => setStep(1)}
                  />
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <FunctionalButton
                    buttonTitle="Invite To Onboard"
                    handleButton={() => setStep(2)}
                    disabled={true}
                  />
                </div>
                <FunctionalButton
                  buttonTitle="Claim In PledgX"
                  handleButton={() => setStep(3)}
                  disabled={true}
                />
              </div>
            )}
            {step === 1 && (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    gap: "16px",
                  }}
                >
                  <Input
                    id="fullWidthInputWrap"
                    label="Account Name"
                    type="text"
                    labelSize="12px"
                    fontSize="18px"
                    validateFunc={(item) => Boolean(item)}
                    setValue={(e) => handleInputChange("companyName", e)}
                    value={form.companyName}
                  />
                </div>

                <AddressAutoFields
                  data={fullAddress}
                  setData={(e, val) => {
                    setFullAddress((address) => {
                      if (e === "company") {
                        return {
                          city: val.city,
                          address: val.address,
                          suite: val.suite,
                          postal: val.postal,
                          country: val.country,
                          province: val.province,
                        };
                      }
                      return {
                        ...address,
                        [e.split(".").slice(1).join(".")]: val,
                      };
                    });
                  }}
                  hasMargin
                />

                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    gap: "16px",
                  }}
                >
                  <Input
                    id="fullWidthInputWrap"
                    label="Account ID"
                    type="text"
                    labelSize="12px"
                    fontSize="18px"
                    validateFunc={(item) => Boolean(item)}
                    setValue={(e) => handleInputChange("accountId", e)}
                    value={form.accountId}
                  />
                  <Select
                    label="Account Type"
                    options={(() =>
                      ["Contract", "Commercial", "Other"].map((type) => ({
                        value: type,
                        label: type,
                      })))()}
                    value={form.accountType}
                    setValue={(e) => handleInputChange("accountType", e)}
                    noCheckIcon={true}
                    showDropdownIndicator={true}
                    validateFunc={(item) => Boolean(item)}
                  />
                  <Input
                    id="fullWidthInputWrap"
                    label="Underwriter Office"
                    type="text"
                    labelSize="12px"
                    fontSize="18px"
                    validateFunc={(item) => item.length > 0}
                    setValue={(e) => handleInputChange("uwOffice", e)}
                    value={form.uwOffice}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    gap: "16px",
                  }}
                >
                  <DateTimePickerField
                    showDateField={true}
                    dateWidth="100%"
                    dateLabel="Submission Date"
                    backgroundColor="rgba(235, 233, 241, 0.75)"
                    dateValue={form.submissionDate}
                    setDateValue={(e) => handleInputChange("submissionDate", e)}
                  />
                  <DateTimePickerField
                    showDateField={true}
                    dateWidth="100%"
                    dateLabel="Financial YE Date"
                    backgroundColor="rgba(235, 233, 241, 0.75)"
                    dateValue={form.financialYeDate}
                    setDateValue={(e) =>
                      handleInputChange("financialYeDate", e)
                    }
                  />
                  <Select
                    label="NAICS Code"
                    options={(() =>
                      naicsCodes.map((code) => ({
                        value: code,
                        label: code,
                      })))()}
                    value={form.naicsCode}
                    setValue={(e) => handleInputChange("naicsCode", e)}
                    noCheckIcon={true}
                    showDropdownIndicator={true}
                    validateFunc={(item) => Boolean(item)}
                  />
                </div>
                <div style={{ width: "100%" }}>
                  <CategorySelect
                    data={form.contractorType}
                    setData={(attribute, val) => {
                      setCategories((old) => [...old, val]);
                    }}
                    notPopup={true}
                    categoriesStandardData={standardCategoriesList}
                    categoriesUNSPCData={unspcFamilyList}
                    containsUNSPC={true}
                  />
                </div>
                <div
                  style={{
                    margin: "40px 0",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <FunctionalButton
                    buttonTitle="Confirm"
                    handleButton={() => handleSubmit(form)}
                    // disabled={
                    //   !form.accountId ||
                    //   !form.companyName ||
                    //   !form.submissionDate ||
                    //   !form.accountType ||
                    //   !form.address ||
                    //   !form.uwOffice ||
                    //   !form.financialYeDate ||
                    //   !form.naicsCode
                    // }
                  />
                </div>
              </>
            )}
          </div>
        </StyledPaper>
      </Dialog>
      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        aria-labelledby="success-dialog"
        aria-describedby="success-dialog"
        maxWidth="md"
        TransitionComponent={React.Fragment}
      >
        <StyledPaper elevation={0}>
          <CloseButton
            aria-label="close"
            onClick={() => setShowSuccessDialog(false)}
          >
            <CloseIcon />
          </CloseButton>
          <Success />
          <FormTitle
            style={{
              marginTop: "10px",
              color: "#1A051D",
              fontSize: "22px",
              fontWeight: "600",
              fontStyle: "normal",
              lineHeight: "24px",
            }}
          >
            Success
          </FormTitle>
          <SmallTitleText
            style={{
              marginBottom: 10,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            The account has been successfully created.
          </SmallTitleText>
          <FunctionalButton
            buttonTitle="Close"
            handleButton={() => setShowSuccessDialog(false)}
            width="308px"
            height="48px"
            isReversedColor={false}
            marginRight={"48px"}
            marginLeft={"48px"}
            marginTop={"30px"}
            marginBottom={"20px"}
            fontSize={"18px"}
            disabled={false}
            icon={null}
            hasBoxShadow={true}
            frontIcon={null}
            weight={600}
          />
        </StyledPaper>
      </Dialog>

      {/* Error Dialog */}
      <AlertPopup
        headerText="Account Creation Unsuccessful"
        height="434px"
        subHeaderText={error}
        openPopup={showErrorDialog}
        setOpenPopup={setShowErrorDialog}
        showIcon={true}
        width={"640px"}
        showButton={false}
      />
    </>
  );
};

const AddressInput = ({ value, setValue }) => {
  const inputRef = useRef(null);

  const loadScript = (url, callback) => {
    let script = document.createElement("script");
    script.type = "text/javascript";

    if (script.readyState) {
      script.onreadystatechange = function () {
        if (
          script.readyState === "loaded" ||
          script.readyState === "complete"
        ) {
          script.onreadystatechange = null;
        }
      };
    } else {
      script.onload = () => callback();
    }

    script.src = url;
    document.getElementsByTagName("head")[0].appendChild(script);
  };

  useEffect(() => {
    loadScript(
      `https://maps.googleapis.com/maps/api/js?key=AIzaSyCwLidsAgErglXnD9dT6qnaQF7u_DYU3Pc&libraries=places`,
      () => {},
    );
  });

  useEffect(() => {
    // Ensure Google Maps API is available
    if (!window.google || !window.google.maps) {
      console.error("Google Maps JavaScript API is not loaded.");
      return;
    }
    // Initialize the Google Places Autocomplete
    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ["geocode"], // Suggest addresses only
        componentRestrictions: { country: "us" }, // Restrict to specific country (optional)
      },
    );
    // Add an event listener to get the selected address
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      setValue(place.formatted_address);
      console.log("Selected Place:", place);
    });
    // Cleanup listener on unmount
    return () => window.google.maps.event.clearInstanceListeners(autocomplete);
  }, []);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "end",
        width: "100%",
      }}
    >
      <InputLabel>Address</InputLabel>
      <input
        type="text"
        ref={inputRef}
        style={{
          width: "100%",
          padding: "1px 40px 1px 15px",
          fontSize: "16px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          height: "48px",
        }}
      />
    </div>
  );
};

const CategorySelect = ({
  data,
  setData,
  attribute,
  usePlusIcon,
  marginTopSecondary,
  isMobile,
  width,
  notPopup,
  readOnly,
  categoriesUNSPCData,
  categoriesStandardData,
  initialSubCategory,
}) => {
  const [enableAdd, setEnableAdd] = useState(true);

  const [containsUNSPC, setContainsUNSPC] = useState(false);

  const onRadioChange = (val) => {
    if (val === 1) {
      setContainsUNSPC(false);
    } else {
      setContainsUNSPC(true);
    }
  };

  const addInitialList = (data) => {
    const tmp = [];
    if (data?.categories?.length > 0) {
      const list = data?.categories;
      const listOfMain = list.map((item) => item.main_categories);
      list.map((item) => {
        tmp.push(
          <CategoriesRow
            attribute={attribute}
            setEnableAdd={setEnableAdd}
            data={data}
            setData={setData}
            categoriesUNSPC={categoriesUNSPCData}
            categoriesStandard={categoriesStandardData}
            initialSubCategory={initialSubCategory}
            containsUNSPC={containsUNSPC}
            onRadioChange={onRadioChange}
            marginTop="0px"
            index={item.index}
            categories={item}
            mainSelected={listOfMain}
            isMobile={isMobile}
            marginTopSecondary={marginTopSecondary}
            readOnly={readOnly}
          />,
        );
      });
    } else {
      tmp.push(
        <CategoriesRow
          attribute={attribute}
          setEnableAdd={setEnableAdd}
          data={data}
          setData={setData}
          categoriesUNSPC={categoriesUNSPCData}
          categoriesStandard={categoriesStandardData}
          initialSubCategory={initialSubCategory}
          containsUNSPC={containsUNSPC}
          onRadioChange={onRadioChange}
          index={0}
          isMobile={isMobile}
          marginTopSecondary={marginTopSecondary}
          readOnly={readOnly}
        />,
      );
    }
    return tmp;
  };

  const [addList, setAddList] = useState([]);

  // Simplified useEffect to prevent infinite loops
  useEffect(() => {
    console.log("CategorySelect data:", data);
    console.log("CategorySelect categories:", data?.categories);
    
    // Always show at least one empty category row
    setAddList(addInitialList(data));
  }, [data?.categories]);

  // Reset when UNSPC setting changes
  useEffect(() => {
    setAddList([]);
  }, [containsUNSPC]);

  const onAdd = () => {
    const index = addList.length;
    const list = data?.categories;
    const listOfMain =
      list?.length > 0 ? list.map((item) => item.main_categories) : [];
    setAddList(
      addList.concat(
        <CategoriesRow
          data={data}
          setData={setData}
          categoriesUNSPC={categoriesUNSPCData}
          categoriesStandard={categoriesStandardData}
          initialSubCategory={initialSubCategory}
          containsUNSPC={containsUNSPC}
          onRadioChange={onRadioChange}
          index={index}
          marginTop="0px"
          marginTopSecondary={marginTopSecondary}
          mainSelected={listOfMain}
          isMobile={isMobile}
          attribute={attribute}
          setEnableAdd={setEnableAdd}
          readOnly={readOnly}
        />,
      ),
    );
  };

  return (
    <>
      <StyledSubtext isMobile={isMobile} width="100%">
        <RadioGroupButtonForProjectCategory
          containsUNSPC={containsUNSPC}
          confirmRadioChange={onRadioChange}
        />
      </StyledSubtext>
      <StyledCategoriesArea isMobile={isMobile} width={width}>
        {addList}
        <PlusSignArea usePlusIcon={usePlusIcon}>
          {usePlusIcon && (
            <>
              <PlusSign enableAdd={enableAdd} onAdd={onAdd} />
              <StyledAddText
                onClick={enableAdd ? onAdd : () => {}}
                enableAdd={enableAdd}
              >
                Add Category
              </StyledAddText>
            </>
          )}
          {!usePlusIcon && (
            <SmallAddText
              onClick={enableAdd ? onAdd : () => {}}
              enableAdd={enableAdd}
            >
              + Add Category
            </SmallAddText>
          )}
        </PlusSignArea>
      </StyledCategoriesArea>
    </>
  );
};
